// AI-GENERATED
// 365 Copilot | GPT-5 Chat model (GPT-5.2)
// Tarkistanut Oskari Rintamäki
// Date: 2026-05-11
//

import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Table, kaReducer } from 'ka-table';
import { SortingMode, PagingPosition } from 'ka-table/enums';

import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import 'swiper/css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faSearchLocation
} from '@fortawesome/free-solid-svg-icons';

import { renderLinksInText } from '../../utils/commonUtil';
import { isMobile, theme } from '../../theme/theme';
import strings from '../../translations';
import PillButton from '../../utils/components/PillButton';
import { executeSearchAction } from '../../utils/gfiUtil';

/* --------------------------------------------------
   Styled components
--------------------------------------------------- */

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

/* ---------- Tabs ---------- */

const StyledTabSwiperContainer = styled.div`
  z-index: 2;
  display: flex;
  background-color: ${(props) => props.theme.colors.mainColor1};
`;

const StyledTabsSwiper = styled(Swiper)`
  width: 100%;
  padding-top: 8px;

  .swiper-slide {
    max-width: 200px;
  }
`;

const StyledSwiperNavigatorButton = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colors.mainColor1};

  svg {
    font-size: 20px;
    color: white;
  }
`;

const StyledGfiTab = styled.div`
  z-index: 10;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  color: ${(props) =>
    props.selected
      ? props.theme.colors.mainColor1
      : props.theme.colors.mainWhite};

  background-color: ${(props) =>
    props.selected
      ? props.theme.colors.mainWhite
      : props.theme.colors.mainColor1};

  border-left: 2px solid ${(props) => props.theme.colors.mainWhite};
  border-top: 2px solid ${(props) => props.theme.colors.mainWhite};
  border-right: 2px solid ${(props) => props.theme.colors.mainWhite};

  padding: 8px 16px 8px 8px;
  border-radius: 4px 4px 0px 0px;
`;

const StyledTabName = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/* ---------- Content ---------- */

const StyledTabContent = styled.div`
  user-select: text;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto; /* grow/shrink and take remaining space */
  min-height: 0; /* critical: allow child to scroll */
  overflow-y: auto; /* scroll when content overflows */

  div.contentWrapper-infobox {
    @media ${(props) => props.theme.device.mobileL} {
      font-size: 14px;
    }
  }

  hr.infoboxLine {
    border: 1px solid;
    margin-top: 10px;
  }

  span.infoboxActionLinks {
    padding-right: 15px;
  }

  table {
    border-top: 1px solid #ddd;
    padding-right: 0px !important;
    width: 100%;
    tr:nth-child(odd) {
      background-color: #f2f2f2;
    }
  }

  .ka-thead-cell {
    background-color: white;
    width: ${(props) => (props.isMobile ? '10em' : 'auto')};
    min-width: 120px;
    span {
      color: ${(props) => props.theme.colors.mainColor1};
    }
  }

  .ka-cell-text {
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
    user-select: text;
    max-height: 150px;
    overflow: hidden;
  }

  .ka-thead-cell-content,
  .ka-cell-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ka-thead-cell-resize {
    width: 5px;
  }

  .low-priority-table {
    margin-left: 0px;
  }

  .ka-thead-cell-content {
    font-size: 14px;
    font-weight: 600;
    color: #212529;
  }

  .ka-table {
    border-top: none;
  }
`;

const StyledShowMoreButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledFeaturesInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

/**
 * Small footer under the table:
 * - Selected count
 * - Clear selections
 * - Center to results
 */
const StyledSelectionFooter = styled.div`
  display: flex;
  flex-direction: ${() => (isMobile ? 'column' : 'row')};
  flex-wrap: wrap;
  gap: 8px;
  align-items: ${(props) => (props.moreFeatures ? 'flex-end' : 'center')};
  justify-content: space-between;
  padding: 10px 12px;
  border-top: 1px solid #cdcdcd;
`;

const StyledSelectionCountTitle = styled.p`
  color: ${(props) => props.theme.colors.mainColor1};
  font-weight: 600;
  margin-right: 8px;
  margin-bottom: 0;
`;

const StyledSelectionCount = styled.p`
  color: ${(props) => props.theme.colors.mainColor1};
  font-weight: 500;
  margin-bottom: 0;
`;

const StyledSelectedCountWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledFooterButtonsWrapper = styled.div`
  display: flex;
  gap: 1rem;
  flexwrap: wrap;
  width: ${() => (isMobile ? '100%' : 'auto')};

  flex-direction: ${() => (isMobile ? 'column' : 'row')};
`;

/* --------------------------------------------------
   Map highlight helpers
--------------------------------------------------- */

function selectFeatures(channel, features, { centerTo = false } = {}) {
  // If no features, ensure previous highlight is cleared.
  if (!features.length) {
    deSelectFeature(channel);
    return;
  }

  // Style mirrors your earlier selection highlight (pink).
  const featureStyle = {
    fill: { color: theme.colors.secondaryColorPink + '80' },
    stroke: {
      color: theme.colors.secondaryColorPink,
      width: 5,
      lineDash: 'solid',
      lineCap: 'round',
      lineJoin: 'round',
      area: {
        color: theme.colors.secondaryColorPink,
        width: 4,
        lineJoin: 'round'
      }
    },
    image: {
      shape: 3,
      size: 5,
      fill: { color: theme.colors.secondaryColorPink }
    }
  };

  // Push overlay features to a dedicated overlay layer.
  channel?.postRequest('MapModulePlugin.AddFeaturesToMapRequest', [
    {
      type: 'FeatureCollection',
      crs: {
        type: 'name',
        properties: { name: 'EPSG:3067' }
      },
      features
    },
    {
      featureStyle,
      layerId: 'gfi-filtered-result-layer-overlay',
      animationDuration: 200,
      clearPrevious: true,
      // Centering options used elsewhere in your codebase:
      centerTo,
      maxZoomLevel: 13
    }
  ]);
}

function deSelectFeature(channel) {
  // Remove highlight overlay layer content.
  channel?.postRequest('MapModulePlugin.RemoveFeaturesFromMapRequest', [
    null,
    null,
    'gfi-filtered-result-layer-overlay'
  ]);
}

/* --------------------------------------------------
   Main component
--------------------------------------------------- */

const FilteredFeatureResults = () => {
  const channel = useSelector((state) => state.rpc.channel);
  const filteredFeatureResults = useSelector(
    (state) => state.rpc.filteredFeatureResults
  );
  const featureFilterPayload = useSelector(
    (state) => state.rpc.featureFilterPayload
  );

  /* ---------------- Tabs state ---------------- */

  const [activeTab, setActiveTab] = useState(0);
  const [tabsSwiper, setTabsSwiper] = useState(null);

  /* ---------------- Selection + hover state ---------------- */

  /**
   * Global selection across ALL tabs:
   * - selectedIds is used for UI (highlighting selected rows across tabs)
   * - selectedFeatureMapRef stores actual feature objects so map highlight works even when tab changes.
   */
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const selectedFeatureMapRef = useRef(new Map());

  // Hover is transient (only the current hovered row).
  const [hoveredId, setHoveredId] = useState(null);

  /* ---------------- Tabs overflow arrows ---------------- */

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrowState = (swiper) => {
    if (!swiper) return;
    setCanScrollLeft(!swiper.isBeginning);
    setCanScrollRight(!swiper.isEnd);
  };

  /* ---------------- Cleanup ---------------- */

  // Clear overlay highlight when component unmounts or channel changes.
  useEffect(() => {
    return () => deSelectFeature(channel);
  }, [channel]);

  useEffect(() => {
    setHoveredId(null);
  }, [activeTab]);

  /* ---------------- Active tab data ---------------- */

  const activeResult = filteredFeatureResults?.[activeTab];

  const features = useMemo(
    () => activeResult?.content?.geojson?.features ?? [],
    [activeResult]
  );

  const featureById = useMemo(() => {
    const map = new Map();
    features.forEach((f) => map.set(f.id, f));
    return map;
  }, [features]);

  /* ---------------- Map sync (selected + hovered) ---------------- */

  useEffect(() => {
    // Compose ids = selectedIds + hoveredId
    const ids = new Set(selectedIds);
    if (hoveredId) ids.add(hoveredId);

    // Resolve features from the global selection map first, then current tab map
    const featuresToShow = [];
    ids.forEach((id) => {
      const fromGlobal = selectedFeatureMapRef.current.get(id);
      const fromCurrent = featureById.get(id);
      const f = fromGlobal || fromCurrent;
      if (f) featuresToShow.push(f);
    });

    featuresToShow.length
      ? selectFeatures(channel, featuresToShow)
      : deSelectFeature(channel);
  }, [selectedIds, hoveredId, featureById, channel]);

  /* ---------------- Table model (columns + rows) ---------------- */

  const columns = useMemo(() => {
    if (!features.length) return [];
    return Object.keys(features[0].properties || {})
      .filter((k) => k !== 'UID')
      .map((key) => ({
        key,
        title: key,
        width: 220
      }));
  }, [features]);

  /**
   * Attach __feature to each row so:
   * - selections can store the correct feature object
   * - format handlers can access the feature if needed in the future
   */
  const rows = useMemo(
    () =>
      features.map((f) => ({
        ...f.properties,
        id: f.id,
        __feature: f
      })),
    [features]
  );

  /* --------------------------------------------------
     ka-table Controlled Mode (kaReducer)
     - Table state (paging/sorting) stored in tableProps
     - Re-renders from selection/hover won't reset paging
  --------------------------------------------------- */

  const [tableProps, setTableProps] = useState(() => ({
    columns: [],
    data: [],
    rowKeyField: 'id',
    sortingMode: SortingMode.SingleTripleState,
    columnResizing: true,
    paging: {
      enabled: true,
      pageIndex: 0,
      pageSize: 100,
      pageSizes: [10, 50, 100],
      position: PagingPosition.Bottom
    }
  }));

  // Sync active tab columns + rows into table props. Reset pageIndex only when tab changes.
  useEffect(() => {
    setTableProps((prev) => ({
      ...prev,
      columns,
      data: rows,
      paging: {
        ...prev.paging,
        pageIndex: 0
      }
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, columns, rows]);

  const dispatch = useCallback((action) => {
    setTableProps((prev) => kaReducer(prev, action));
  }, []);

  if (!filteredFeatureResults?.length) return null;

  const cellClampStyle = {
    maxHeight: '40px',
    lineHeight: '16px',
    WebkitLineClamp: 3,
    overflow: 'auto'
  };

  /* ---------------- Actions: load more / clear / center ---------------- */

  const handleSearchMoreFeatures = () => {
    const payload = featureFilterPayload.layers?.filter(
      (p) => p.layerId === activeResult?.content?.layerId
    );

    executeSearchAction(
      { layers: payload },
      channel,
      activeResult?.content?.nextStartIndex
    );
  };

  const handleClearSelections = () => {
    selectedFeatureMapRef.current.clear();
    setSelectedIds(new Set());
    setHoveredId(null);
    deSelectFeature(channel);
  };

  const handleCenterToSelections = () => {
    const selectedFeatures = Array.from(selectedFeatureMapRef.current.values());
    if (!selectedFeatures.length) return;
    selectFeatures(channel, selectedFeatures, { centerTo: true });
  };

  const moreFeatures = activeResult?.content?.moreFeatures;

  /* --------------------------------------------------
     Render
  --------------------------------------------------- */

  return (
    <RootContainer id="filtered-feature-results-root">
      {/* ---------- Tabs ---------- */}
      <StyledTabSwiperContainer id="filtered-feature-results-tabs-container">
        {canScrollLeft && (
          <StyledSwiperNavigatorButton
            id="filtered-feature-results-tabs-prev"
            role="button"
            aria-label="Scroll tabs left"
            onClick={() => tabsSwiper?.slidePrev()}
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </StyledSwiperNavigatorButton>
        )}

        <StyledTabsSwiper
          id="filtered-feature-results-tabs-swiper"
          slidesPerView="auto"
          spaceBetween={4}
          freeMode
          modules={[FreeMode]}
          onSwiper={(swiper) => {
            setTabsSwiper(swiper);
            updateArrowState(swiper);
          }}
          onSlideChange={updateArrowState}
          onResize={updateArrowState}
        >
          {filteredFeatureResults.map((res, index) => {
            const layerId = res?.content?.layerId ?? res?.layerId ?? index;
            const isActive = activeTab === index;

            return (
              <SwiperSlide
                key={layerId}
                id={`filtered-feature-results-tab-slide-${layerId}`}
              >
                <StyledGfiTab
                  id={`filtered-feature-results-tab-${layerId}`}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="filtered-feature-results-table-container"
                  selected={isActive}
                  onClick={() => setActiveTab(index)}
                >
                  <StyledTabName
                    id={`filtered-feature-results-tab-name-${layerId}`}
                  >
                    {res?.content?.layerName ?? `Layer ${index + 1}`}
                  </StyledTabName>
                </StyledGfiTab>
              </SwiperSlide>
            );
          })}
        </StyledTabsSwiper>

        {canScrollRight && (
          <StyledSwiperNavigatorButton
            id="filtered-feature-results-tabs-next"
            role="button"
            aria-label="Scroll tabs right"
            onClick={() => tabsSwiper?.slideNext()}
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </StyledSwiperNavigatorButton>
        )}
      </StyledTabSwiperContainer>

      {/* ---------- Table ---------- */}
      <StyledTabContent id="filtered-feature-results-table-container">
        <Table
          id="filtered-feature-results-ka-table"
          {...tableProps}
          dispatch={dispatch}
          format={({ value }) => renderLinksInText(value)}
          childComponents={{
            cell: {
              elementAttributes: ({ rowData, column }) => {
                const rowId = rowData?.id;
                const colKey = column?.key ?? 'unknown';
                const isSelected = selectedIds.has(rowId);
                const isHovered = hoveredId === rowId;

                return {
                  id: `filtered-feature-results-cell-${rowId}-${colKey}`,

                  // Hover = transient highlight, never clears persistent selection
                  onMouseEnter: () => setHoveredId(rowId),
                  onMouseLeave: () => setHoveredId(null),

                  // Click toggles global selection (across all tabs)
                  onClick: () => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);

                      if (next.has(rowId)) {
                        next.delete(rowId);
                        selectedFeatureMapRef.current.delete(rowId);
                      } else {
                        next.add(rowId);

                        // Store the actual feature so it can be highlighted even after tab changes
                        const f = rowData?.__feature || featureById.get(rowId);
                        if (f) selectedFeatureMapRef.current.set(rowId, f);
                      }

                      return next;
                    });
                  },

                  style: {
                    ...cellClampStyle,
                    cursor: 'pointer',
                    backgroundColor:
                      isSelected || isHovered
                        ? theme.colors.mainColor3transparent30
                        : undefined
                  }
                };
              }
            }
          }}
        />

        {/* ---------- Selection footer (count + actions) ---------- */}
        <StyledSelectionFooter
          id="filtered-feature-results-selection-footer"
          moreFeatures={moreFeatures}
        >
          <StyledFeaturesInfo>
            <StyledSelectedCountWrapper>
              <StyledSelectionCountTitle>
                {strings?.gfi?.selectedCount}
              </StyledSelectionCountTitle>
              <StyledSelectionCount id="filtered-feature-results-selected-count">
                {selectedIds.size}
              </StyledSelectionCount>
            </StyledSelectedCountWrapper>
            {moreFeatures && (
              <StyledShowMoreButtonWrapper id="filtered-feature-results-load-more-wrapper">
                <PillButton
                  id="filtered-feature-results-load-more-button"
                  text={strings.gfi.getMoreFeatures}
                  onClick={handleSearchMoreFeatures}
                  aria-label={strings.gfi.getMoreFeatures}
                  style={{
                    justifyContent: isMobile ? 'center' : 'flex-start',
                    alignItems: 'center'
                  }}
                />
              </StyledShowMoreButtonWrapper>
            )}
          </StyledFeaturesInfo>

          <StyledFooterButtonsWrapper>
            <PillButton
              id="filtered-feature-results-clear-selections-button"
              text={strings?.gfi?.clearSelections}
              variant="inverse"
              onClick={handleClearSelections}
              aria-label={strings?.gfi?.clearSelections}
              disabled={selectedIds.size === 0}
              style={{
                justifyContent: isMobile ? 'center' : 'flex-start',
                alignItems: 'center'
              }}
            />

            <PillButton
              id="filtered-feature-results-center-to-results-button"
              icon={faSearchLocation}
              text={strings?.gfi?.focusToLocations}
              variant="inverse"
              onClick={handleCenterToSelections}
              aria-label={strings?.gfi?.focusToLocations}
              disabled={selectedIds.size === 0}
              style={{
                justifyContent: isMobile ? 'center' : 'flex-start',
                alignItems: 'center'
              }}
            />
          </StyledFooterButtonsWrapper>
        </StyledSelectionFooter>
      </StyledTabContent>
    </RootContainer>
  );
};

export default FilteredFeatureResults;
